<template>
  <v-layout align-left column wrap>
    <v-flex>
      <v-card flat>
        <v-card-title>
          Trainees
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            v-if="!isNew && formData.trainees.length === 0"
            @click="openDialog()"
          >
            <v-icon>mdi-plus</v-icon>
            add trainee
          </v-btn>
        </v-card-title>
        <section :class="[!isNew ? 'stacked' : '', 'avatars-group pa-3']">
          <div
            v-for="trainee in formData.trainees"
            :key="trainee.employee"
            class="avatars-group__item"
          >
            <v-menu
              :close-on-content-click="false"
              :nudge-width="100"
              open-on-hover
              left
              offset-x
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <div class="d-flex align-center mb-4">
                  <v-avatar
                    v-on="on"
                    :color="userColor(trainee.name)"
                    v-bind="attrs"
                    dark
                  >
                    <span class="white--text">{{
                      userName(trainee.name)
                    }}</span>
                  </v-avatar>
                  <div
                    v-if="formData.status === 'Released'"
                    class="d-flex align-center ml-4"
                  >
                    <v-tooltip v-if="!trainee.hasAttachments" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <div
                          class="file-upload-wrapper"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <input
                            type="file"
                            ref="fileInput"
                            :id="'file-input-' + trainee.traineeNo"
                            style="display: none"
                            accept="image/jpeg,image/png,application/pdf"
                            @change="(event) => uploadFile(event, trainee)"
                          />
                          <v-btn
                            small
                            outlined
                            color="primary"
                            @click="triggerFileInput(trainee.traineeNo)"
                            class="file-upload-btn"
                          >
                            <v-icon small>mdi-camera</v-icon>
                          </v-btn>
                          <div
                            v-if="uploadedFiles[trainee.traineeNo]"
                            class="file-info"
                          ></div>
                        </div>
                      </template>
                      <span>Upload Document</span>
                    </v-tooltip>
                    <v-tooltip v-else bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <div class="file-download" v-bind="attrs" v-on="on">
                          <v-btn
                            small
                            outlined
                            color="primary"
                            @click="downloadUploadedReport(trainee)"
                            class="file-upload-btn"
                          >
                            <v-icon small>mdi-download</v-icon>
                          </v-btn>
                        </div>
                      </template>
                      <span>Download Report</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          @click="sendDocForApproval(trainee)"
                          class="mx-1"
                          color="yellow lighten-4"
                          label
                          small
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon color="yellow darken-4" small>
                            mdi-send
                          </v-icon>
                        </v-chip>
                      </template>
                      <span>Send For approval</span>
                    </v-tooltip>
                  </div>
                </div>
              </template>

              <v-card
                class="mx-auto"
                :height="isEdit && !isNew ? '170' : '130'"
                flat
              >
                <v-sheet
                  color="primary"
                  class="mx-auto px-3 d-flex"
                  height="40"
                  elevation="0"
                >
                  <v-avatar class="mt-3" color="white" size="50">
                    <v-avatar size="44" :color="userColor(trainee.name)">
                      <span class="white--text">
                        {{ userName(trainee.name) }}
                      </span>
                    </v-avatar>
                  </v-avatar>
                </v-sheet>
                <v-card-text
                  class="mt-1"
                  align="right"
                  :height="isEdit ? '170' : '130'"
                >
                  {{ trainee.employee }}
                  <br />
                  {{ trainee.name }}
                </v-card-text>
                <v-divider v-if="isEdit && !isNew" />
                <v-card-actions class="pt-3" v-if="isEdit && !isNew">
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    small
                    depressed
                    @click="deleteTrainee(trainee)"
                  >
                    remove
                    <v-icon size="15" class="ml-1"> mdi-delete </v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </div>

          <!-- add trainees -->
          <div class="avatars-group__item more">
            <v-avatar
              color="primary"
              size="40px"
              v-if="formData.trainees.length !== 0 && !isNew"
            >
              <v-btn icon @click="openDialog">
                <v-icon
                  :color="$vuetify.theme.dark ? 'grey darken-2' : 'white'"
                >
                  mdi-plus
                </v-icon>
              </v-btn>
            </v-avatar>
          </div>
        </section>

        <!-- employees dialog -->
        <v-dialog
          v-model="dialog"
          persistent
          width="80%"
          :fullscreen="$vuetify.breakpoint.smAndDown"
        >
          <v-card>
            <v-card-title>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
              ></v-text-field>
              <v-spacer></v-spacer>
              <v-btn
                depressed
                @click="dialog = false"
                class="deep-orange lighten-4 deep-orange--text text--darken-4"
              >
                close
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text
              class="overflow-y-auto overflow-x-hidden pt-5"
              style="max-height: 70vh"
            >
              <v-data-table
                dense
                :headers="headers"
                :items="employees"
                :search="search"
                :items-per-page="8"
                :footer-props="{
                  showFirstLastPage: true,
                  'items-per-page-text': 'Employees per page',
                  'items-per-page-options': [5, 8, 10],
                }"
                item-key="number"
              >
                <template v-slot:item.filtered="{ item }">
                  <v-layout>
                    <v-checkbox
                      small
                      class="shrink mr-2"
                      v-model="item.filtered"
                      :disabled="
                        item.number !== user.number &&
                        !canEdit &&
                        isEdit &&
                        !isSupervisor
                      "
                      @change="check($event, item)"
                    ></v-checkbox>
                  </v-layout>
                </template>
              </v-data-table>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" depressed v-if="!isEdit" @click="submit()">
                Submit
              </v-btn>
              <v-btn color="primary" depressed v-else @click="dialog = false">
                Done
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card>
    </v-flex>
  </v-layout>
</template>
<script>
import helper from "@kinetics254/cassandra-base/utilities/helpers/helper";
import helpers from "@kinetics254/cassandra-base/utilities/helpers/helper";

export default {
  name: "Trainee",
  props: {
    formData: Object,
    isEdit: Boolean,
    isSupervisor: Boolean,
    canEdit: Boolean,
    user: Object,
    isNew: Boolean,
  },
  data() {
    return {
      dialog: false,
      search: "",
      chosenOnes: [],
      uploadedFiles: {}, // Store uploaded files by item no
    };
  },
  computed: {
    trainees() {
      return this.$store.getters["Setup/setupGetter"]("trainees");
    },
    employees() {
      return this.$store.getters["Setup/setupGetter"]("leaveEmployees").map(
        (item) => {
          const filtered = this.formData.trainees.some((itemInner) => {
            return itemInner.employee === item.number;
          });
          return {
            ...item,
            filtered,
            fullName:
              item.first_name + " " + item.middle_name + " " + item.last_name,
          };
        }
      );
    },
    headers() {
      return [
        {
          text: "Employee Number",
          value: "number",
          sortable: true,
          align: "start",
        },
        {
          text: "Name",
          value: "fullName",
          sortable: true,
          align: "start",
        },
        {
          text: "Email",
          value: "company_email",
          sortable: true,
          align: "start",
        },
        {
          text: "Select",
          value: "filtered",
          sortable: true,
          align: "start",
        },
      ];
    },
  },
  methods: {
    check(event, item) {
      if (this.isEdit) {
        const data = {
          documentNo: this.formData.no,
          employee: item.number,
        };
        if (!event) {
          this.deleteTrainee(data);
        } else {
          this.$store.dispatch("Training/addTrainee", data);
        }
      } else {
        if (event) {
          this.chosenOnes.push(item);
        } else {
          const data = this.chosenOnes.filter((employee) => {
            return employee.number === item.number;
          })[0];
          const index = Array.prototype.indexOf.call(this.chosenOnes, data);
          this.chosenOnes.splice(index, 1);
        }
      }
    },
    deleteTrainee(data) {
      this.$store.dispatch("Training/deleteTrainee", data);
    },
    openDialog() {
      this.dialog = true;
    },
    submit() {
      this.formData.trainees = this.chosenOnes.map((item) => {
        return {
          employee: item.number,
          name: item.first_name + " " + item.middle_name + " " + item.last_name,
        };
      });
      this.dialog = false;
    },
    allowMultiple(item) {
      return !this.canEdit;
    },
    userColor(name) {
      if (name) {
        return helper.stringToColour(name);
      }
    },
    userName(name) {
      if (name) {
        const words = name.split(" ");
        const letters = words.map((word) => word[0]);
        return letters.join("");
      }
    },
    // Trigger file input click
    triggerFileInput(itemNo) {
      const fileInput = document.getElementById(`file-input-${itemNo}`);
      if (fileInput) {
        fileInput.click();
      }
    },

    // Handle file upload
    uploadFile: async function (event, item) {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
        if (!allowedTypes.includes(file.type)) {
          alert("Please select a valid file type (JPEG, PNG, or PDF)");
          return;
        }

        // Validate file size (e.g., 5MB limit)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
          alert("File size must be less than 5MB");
          return;
        }

        try {
          // Convert to base64
          const base64 = await helpers.toBase64(file);
          const cleanedBase64 = base64.replace(
            /^data:application\/pdf;base64,/,
            ""
          );

          // Store file information
          this.$set(this.uploadedFiles, item.no, {
            name: file.name,
            type: file.type,
            size: file.size,
            base64: cleanedBase64,
            lastModified: file.lastModified,
          });

          const payload = {
            fileName: file.name,
            documentType: "",
            attachment: cleanedBase64,
            no: item.traineeNo,
            attType: "trainee",
            docNo: this.formData.no,
          };

          this.$store.dispatch("Training/uploadTrainingDoc", payload);
        } catch (error) {
          console.error("Error uploading file:", error);
          alert("Error uploading file. Please try again.");
        }

        // Reset the input
        event.target.value = "";
      }
    },

    sendDocForApproval: function (item) {
      console.log("trainees", item.traineeNo);
      const traineeNo = item.traineeNo;

      if (!traineeNo) {
        alert("Please add trainee to the programme");
        return;
      }

      const payload = {
        traineeNo: traineeNo,
      };

      this.$store.dispatch("Training/sendDocForApproval", payload);
    },

    downloadUploadedReport: function (item) {
      this.$store.dispatch("Training/downloadUploadedReport", item.traineeNo);
    },
  },
};
</script>
